<template>
  <div>
    <!-- Filters sit above the table rather than inside it: DataTable owns the
         title strip and the rows, and a page's own filters are the page's. Four
         short lists, no date range, so a full FilterBar would be more chrome
         than this needs. -->
    <div class="kf-filters">
      <div class="kf-field">
        <span class="kf-field__label">Status</span>
        <v-select
          v-model="filters.status"
          :items="statusItems"
          hide-details
          dense
          outlined
          clearable
          placeholder="All"
          @change="reload"
        />
      </div>
      <div class="kf-field">
        <span class="kf-field__label">Channel</span>
        <v-select
          v-model="filters.channel"
          :items="channels"
          item-text="label"
          item-value="value"
          hide-details
          dense
          outlined
          clearable
          placeholder="All"
          @change="reload"
        />
      </div>
      <div class="kf-field">
        <span class="kf-field__label">Stage</span>
        <v-select
          v-model="filters.stage"
          :items="stages"
          hide-details
          dense
          outlined
          clearable
          placeholder="All"
          @change="reload"
        />
      </div>
      <div class="kf-field">
        <span class="kf-field__label">Language</span>
        <v-select
          v-model="filters.language"
          :items="['en', 'hi']"
          hide-details
          dense
          outlined
          clearable
          placeholder="All"
          @change="reload"
        />
      </div>
      <v-btn
        v-if="hasFilters"
        text
        x-small
        class="text-none kf-filters__reset"
        @click="resetFilters"
      >
        Reset
      </v-btn>
    </div>

    <DataTable
      title="Template Library"
      :headers="headers"
      :items="items"
      :loading="loading"
      :error="error"
      :page="page"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-length="pageLength"
      empty-title="No templates yet"
      empty-message="This is the library the growth agent picks from. Import a batch or write one; a complete template is live as soon as it is saved."
      empty-icon="file-text"
      @page-change="onPage"
      @retry="load"
    >
      <template #actions>
        <v-text-field
          v-model="search"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          hide-details
          dense
          outlined
          clearable
          class="kf-inline-search"
          @input="debouncedReload"
        />
        <template v-if="canEdit">
          <v-btn small outlined class="text-none ml-2" @click="bulk_dialog = true">
            <LucideIcon name="upload" :size="16" class="mr-1" />
            Import
          </v-btn>
          <v-btn small depressed color="primary" class="text-none ml-2" @click="openAdd">
            <LucideIcon name="plus" :size="16" class="mr-1" />
            New template
          </v-btn>
        </template>
      </template>

      <template #[`item.name`]="{ item }">
        <div>
          <div class="kf-name">{{ item.name }}</div>
          <div class="kf-sub">{{ item.intent }}<template v-if="item.purpose"> · {{ item.purpose }}</template></div>
        </div>
      </template>

      <template #[`item.channel`]="{ item }">
        <span>{{ item.channel }}</span>
      </template>

      <template #[`item.stage_tags`]="{ item }">
        <span v-if="!(item.stage_tags || []).length" class="kf-any">any stage</span>
        <span v-else class="kf-tags">{{ item.stage_tags.join(", ") }}</span>
      </template>

      <template #[`item.body`]="{ item }">
        <div class="kf-body" :title="item.body">
          <span v-if="item.subject" class="kf-body__subject">{{ item.subject }}</span>
          {{ item.body }}
        </div>
      </template>

      <!-- Inactive says why, on the row: turned off by someone, or what is
           missing (a DLT id, a registered WhatsApp name, a purpose ...). -->
      <template #[`item.status`]="{ item }">
        <StatusChip :status="item.status" :color="statusColor[item.status]" small />
        <ul v-if="item.status === 'inactive' && (item.inactive_reasons || []).length"
            class="kf-reasons">
          <li v-for="reason in item.inactive_reasons" :key="reason">{{ reason }}</li>
        </ul>
      </template>

      <template #[`item.modified`]="{ item }">
        <span :title="fmt.dateTime(item.modified)">{{ fmt.relative(item.modified) }}</span>
      </template>

      <template #[`item.actions`]="{ item }">
        <div class="text-end" @click.stop>
          <v-menu offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                <LucideIcon name="ellipsis-vertical" :size="18" />
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="openPerformance(item)">
                <LucideIcon name="chart-line" :size="16" class="mr-2" />
                <v-list-item-title class="kf-menu">Performance</v-list-item-title>
              </v-list-item>

              <template v-if="canEdit">
                <v-divider />

                <v-list-item @click="openEdit(item)">
                  <LucideIcon name="pencil" :size="16" class="mr-2" />
                  <v-list-item-title class="kf-menu">Edit</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="item.turned_off" @click="turn(item, true)">
                  <LucideIcon name="circle-play" :size="16" class="mr-2" color="#2E7D32" />
                  <v-list-item-title class="kf-menu">Turn on</v-list-item-title>
                </v-list-item>
                <v-list-item v-else @click="turn(item, false)">
                  <LucideIcon name="circle-pause" :size="16" class="mr-2" color="#C0392B" />
                  <v-list-item-title class="kf-menu kf-menu--danger">Turn off</v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </div>
      </template>
    </DataTable>

    <TemplateFormDialog
      v-model="form_dialog"
      :template="editing"
      :draft="draft"
      @saved="reload"
    />

    <TemplateBulkUploadDialog v-model="bulk_dialog" @imported="reload" />

    <TemplatePerformanceDialog v-model="perf_dialog" :template="target" />

  </div>
</template>

<script>
/**
 * Template Library — the copy the growth agent picks from.
 *
 * Since 23 Oct 2026 a template is Active or Inactive and nothing else. Active:
 * switched on and nothing it needs is missing; the agent may pick it. Inactive:
 * turned off by someone, or missing something (a DLT id, a registered WhatsApp
 * name, a purpose, a blank nothing fills) -- the reasons are on the row, so the
 * page says what to fix. A complete template is live as soon as it is saved,
 * and editing one changes it in place.
 *
 * Read-only without the edit permission: the list and performance stay visible
 * (they are what most people come here for), and every action that changes what
 * borrowers receive disappears.
 */
import DataTable from "@/components/karrofin/DataTable.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";
import TemplateFormDialog from "@/components/karrofin/TemplateFormDialog.vue";
import TemplateBulkUploadDialog from "@/components/karrofin/TemplateBulkUploadDialog.vue";
import TemplatePerformanceDialog from "@/components/karrofin/TemplatePerformanceDialog.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";
import { PAGE_LENGTH } from "@/app-js/karrofin/config";
import {
  CHANNELS,
  TEMPLATE_DRAFT_KEY,
  TEMPLATE_STAGES,
  TEMPLATE_STATUSES,
  TEMPLATE_STATUS_COLOR,
  TEMPLATE_STATUS_HELP,
} from "@/app-js/karrofin/constants";
import _ from "lodash";

/** Without this the screen is read-only. Seeded by seed_karrofin_permissions. */
const EDIT_PERMISSION = "KarroFin Templates Edit";

export default {
  name: "KarroFinTemplates",
  components: {
    DataTable,
    StatusChip,
    TemplateFormDialog,
    TemplateBulkUploadDialog,
    TemplatePerformanceDialog,
  },
  data() {
    return {
      fmt,
      loading: true,
      error: null,
      items: [],
      page: 1,
      totalPages: 1,
      totalCount: null,
      pageLength: PAGE_LENGTH,
      search: "",
      canEdit: false,

      filters: { status: null, channel: null, stage: null, language: null },

      form_dialog: false,
      bulk_dialog: false,
      perf_dialog: false,
      // The row a dialog is about.
      editing: null,
      // A suggested template handed over by the Agent Tracker (TEMPLATE_DRAFT_KEY).
      draft: null,
      target: null,

      channels: CHANNELS,
      stages: TEMPLATE_STAGES,
      statusItems: TEMPLATE_STATUSES.map((s) => ({
        text: s.charAt(0).toUpperCase() + s.slice(1),
        value: s,
      })),
      statusHelp: TEMPLATE_STATUS_HELP,
      statusColor: TEMPLATE_STATUS_COLOR,

      headers: [
        { text: "Template", value: "name" },
        { text: "Channel", value: "channel", width: 110 },
        { text: "Stages", value: "stage_tags", width: 160 },
        { text: "Message", value: "body" },
        { text: "Status", value: "status", width: 240 },
        { text: "Updated", value: "modified", width: 110 },
        { text: "", value: "actions", align: "end", width: 56 },
      ],
    };
  },
  computed: {
    hasFilters() {
      return Object.keys(this.filters).some((k) => this.filters[k]);
    },
  },
  created() {
    const permissions = JSON.parse(sessionStorage.getItem("permission_list")) || [];
    this.canEdit = permissions.indexOf(EDIT_PERMISSION) !== -1;
    this.debouncedReload = _.debounce(this.reload, 400);
    this.load();
    this.openHandedDraft();
  },
  beforeDestroy() {
    if (this.debouncedReload) this.debouncedReload.cancel();
  },
  methods: {
    load() {
      this.loading = true;
      this.error = null;
      // Started off a resolved promise so a synchronous throw inside the call
      // becomes a rejection this chain can see -- otherwise a function missing
      // from api/index.js's default export map leaves the spinner running
      // forever with nothing in the network tab.
      Promise.resolve()
        .then(() =>
          api.getTemplates({
            page_number: this.page,
            page_length: this.pageLength,
            search: this.search || undefined,
            status: this.filters.status || undefined,
            channel: this.filters.channel || undefined,
            stage: this.filters.stage || undefined,
            language: this.filters.language || undefined,
          })
        )
        .then((data) => {
          this.items = data.results;
          this.totalPages = data.total_page_count;
          this.totalCount = data.total_count;
        })
        .catch((err) => {
          this.error = err;
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    reload() {
      this.page = 1;
      this.load();
    },
    onPage(page) {
      this.page = page;
      this.load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    resetFilters() {
      this.filters = { status: null, channel: null, stage: null, language: null };
      this.reload();
    },
    openAdd() {
      this.editing = null;
      this.draft = null;
      this.form_dialog = true;
    },
    /** Opened from the Agent Tracker's "Add template": start the form filled in. */
    openHandedDraft() {
      if (this.$route.query.draft !== "1") return;
      let draft = null;
      try {
        draft = JSON.parse(sessionStorage.getItem(TEMPLATE_DRAFT_KEY) || "null");
        sessionStorage.removeItem(TEMPLATE_DRAFT_KEY);
      } catch (e) {
        draft = null;
      }
      const query = Object.assign({}, this.$route.query);
      delete query.draft;
      this.$router.replace({ query }).catch(() => {});
      if (!draft) return;
      if (!this.canEdit) {
        this.showSnakeBar("error", "You need template edit access to add a template");
        return;
      }
      this.editing = null;
      this.draft = draft;
      this.form_dialog = true;
    },
    openEdit(item) {
      this.editing = item;
      this.form_dialog = true;
    },
    openPerformance(item) {
      this.target = item;
      this.perf_dialog = true;
    },
    /** Turn a template on or off. On: live only if nothing is missing. */
    turn(item, on) {
      Promise.resolve()
        .then(() => api.switchTemplate(item.id, on))
        .then((result) => {
          const t = (result && result.template) || {};
          if (!on) {
            this.showSnakeBar("success", `“${item.name}” turned off`);
          } else if (t.status === "active") {
            this.showSnakeBar("success", `“${item.name}” is live`);
          } else {
            this.showSnakeBar(
              "error",
              `“${item.name}” is still inactive: ${(t.inactive_reasons || []).join(" ")}`
            );
          }
          this.load();
        })
        .catch((err) => {
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) || "Could not change the template"
          );
        });
    },
  },
};
</script>

<style scoped>
.kf-inline-search {
  max-width: 200px;
}
.kf-filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 16px 12px;
}
.kf-field {
  min-width: 150px;
}
.kf-field__label {
  display: block;
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: var(--kf-text-muted);
  margin-bottom: 4px;
}
.kf-filters__reset {
  margin-bottom: 4px;
}
.kf-name {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  color: #000;
  white-space: nowrap;
}
.kf-sub {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  font-family: monospace;
}
.kf-reasons {
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: var(--kf-fs-label);
  line-height: 1.35;
  color: #8a3b12;
}
.kf-any {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  font-style: italic;
}
.kf-tags {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-body);
}
.kf-body {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--kf-fs-label);
  color: var(--kf-text-body);
}
.kf-body__subject {
  font-weight: 600;
  color: #000;
  margin-right: 4px;
}
.kf-menu {
  font-size: var(--kf-fs-hint);
}
.kf-menu--danger {
  color: var(--kf-danger);
}
</style>
